<template>
  <div class="expert-manager space-y-6 animate-[fadeIn_0.6s_ease-out]">
    <!-- 前台关联提示 -->
    <div class="bg-blue-50/80 backdrop-blur-sm border border-blue-100/60 rounded-2xl p-4 flex items-start gap-3 text-sm text-blue-700">
      <svg class="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <div>
        <p class="font-medium mb-1">前台关联</p>
        <p class="text-blue-600">专家信息展示在前台「专家排行榜」和「专家详情页」，直接影响用户的学习推荐和预约决策</p>
      </div>
    </div>

    <!-- 搜索 + 操作栏 -->
    <div class="flex items-center justify-between gap-4">
      <div class="relative flex-1 max-w-md">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="搜索姓名、头衔、公司或擅长领域..."
          class="w-full px-4 py-2 pl-10 bg-white border border-slate-200 rounded-full text-sm focus:outline-none focus:border-[#F2C94C] focus:ring-2 focus:ring-[#F2C94C]/20 transition-all"
        />
        <svg class="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>
      <div class="flex items-center gap-2">
        <button
          @click="handleExport"
          class="flex items-center gap-1.5 px-3 py-2 border border-slate-200 text-slate-600 text-sm rounded-full hover:bg-slate-50 transition-colors"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          导出数据
        </button>
        <button
          @click="showBatchImport = true"
          class="flex items-center gap-1.5 px-3 py-2 border border-slate-200 text-slate-600 text-sm rounded-full hover:bg-slate-50 transition-colors"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
          批量导入
        </button>
        <button
          v-if="hasPerm(PERMISSIONS.CREATE_EXPERT)"
          @click="handleAdd"
          class="flex items-center gap-1.5 px-4 py-2 bg-[#F2C94C] text-white text-sm font-medium rounded-full hover:bg-[#E5B73B] transition-colors shadow-sm"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          新增专家
        </button>
      </div>
    </div>

    <!-- 专家列表 -->
    <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
      <el-table :data="filteredExperts" stripe size="small" style="width: 100%">
        <el-table-column prop="name" label="姓名" min-width="100">
          <template #default="{ row }">
            <span class="text-sm font-medium text-slate-900">{{ row.name }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="title" label="头衔" min-width="160" />

        <el-table-column prop="department" label="所属公司" width="120" />

        <el-table-column label="擅长领域" width="140">
          <template #default="{ row }">
            <div class="flex flex-wrap gap-1">
              <span
                v-for="t in row.topics?.slice(0, 2)"
                :key="t"
                class="inline-flex items-center px-1.5 py-0.5 bg-slate-100 text-slate-500 text-xs rounded"
              >
                {{ t }}
              </span>
              <span v-if="row.topics?.length > 2" class="text-xs text-slate-400">+{{ row.topics.length - 2 }}</span>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="积分" width="90" align="center">
          <template #default="{ row }">
            <el-tooltip placement="top">
              <div slot="content" class="text-xs space-y-1">
                <div>点击: {{ row.stats.clicks }} × 1 = {{ row.stats.clicks }}</div>
                <div>播放: {{ row.stats.plays }} × 2 = {{ row.stats.plays * 2 }}</div>
                <div>收藏: {{ row.stats.bookmarks }} × 5 = {{ row.stats.bookmarks * 5 }}</div>
                <div>点赞: {{ row.stats.likes }} × 5 = {{ row.stats.likes * 5 }}</div>
                <div>评论: {{ row.stats.comments }} × 5 = {{ row.stats.comments * 5 }}</div>
                <div class="border-t border-slate-600 pt-1 mt-1">总计: {{ row.points }}</div>
              </div>
              <span class="text-sm font-medium text-[#F2C94C] cursor-help">{{ row.points }}</span>
            </el-tooltip>
          </template>
        </el-table-column>

        <el-table-column label="状态" width="80" align="center">
          <template #default="{ row }">
            <span
              :class="[
                'inline-flex items-center px-2 py-0.5 text-xs rounded font-medium',
                row.status === 'active' ? 'bg-emerald-50 text-emerald-600' :
                row.status === 'suspended' ? 'bg-amber-50 text-amber-600' :
                'bg-red-50 text-red-500'
              ]"
            >
              {{ statusLabel(row.status) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="120" fixed="right">
          <template #default="{ row }">
            <div class="flex items-center gap-2">
              <button
                v-if="hasPerm(PERMISSIONS.EDIT_EXPERT)"
                @click="handleEdit(row)"
                class="text-[#F2C94C] hover:text-[#E5B73B] text-sm font-medium transition-colors"
              >
                编辑
              </button>
              <button
                v-if="hasPerm(PERMISSIONS.DELETE_EXPERT)"
                @click="handleDelete(row)"
                class="text-red-400 hover:text-red-500 text-sm font-medium transition-colors"
              >
                删除
              </button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- 空状态 -->
      <div v-if="filteredExperts.length === 0" class="text-center py-12">
        <svg class="w-12 h-12 text-slate-200 mx-auto mb-3" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
        <div class="text-slate-400 text-sm">暂无专家</div>
      </div>
    </div>

    <!-- 批量导入对话框 -->
    <el-dialog
      title="批量导入专家"
      :visible.sync="showBatchImport"
      width="600px"
      :close-on-click-modal="false"
    >
      <div class="space-y-4">
        <div class="flex items-center justify-between">
          <div class="bg-amber-50 border border-amber-100 rounded-lg p-3 text-sm text-amber-700 flex-1 mr-4">
            <p class="font-medium mb-1">导入说明</p>
            <ul class="list-disc list-inside space-y-0.5 text-amber-600">
              <li>请先下载模板，按模板格式填写数据</li>
              <li>支持 .xlsx 格式文件</li>
              <li>导入的专家默认状态为「正常」</li>
            </ul>
          </div>
          <button
            @click="downloadTemplate"
            class="flex items-center gap-1.5 px-3 py-2 border border-slate-200 text-slate-600 text-sm rounded-full hover:bg-slate-50 transition-colors"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            下载模板
          </button>
        </div>

        <div class="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center hover:border-[#F2C94C] transition-colors">
          <input
            ref="fileInput"
            type="file"
            accept=".xlsx,.xls"
            class="hidden"
            @change="handleFileSelect"
          />
          <svg class="w-10 h-10 text-slate-300 mx-auto mb-3" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <p class="text-sm text-slate-500 mb-2">拖拽文件到此处，或</p>
          <button
            @click="$refs.fileInput.click()"
            class="px-4 py-1.5 bg-[#F2C94C] text-white text-sm rounded-full hover:bg-[#E5B73B] transition-colors"
          >
            选择文件
          </button>
        </div>

        <div v-if="importResult" class="bg-slate-50 rounded-lg p-3 text-sm">
          <p class="font-medium text-slate-700 mb-1">导入结果</p>
          <p class="text-emerald-600">成功导入 {{ importResult.success }} 条</p>
          <p v-if="importResult.failed > 0" class="text-red-500">失败 {{ importResult.failed }} 条</p>
          <div v-if="importResult.errors.length > 0" class="mt-2 space-y-1">
            <p v-for="(err, i) in importResult.errors.slice(0, 5)" :key="i" class="text-xs text-red-400">{{ err }}</p>
          </div>
        </div>
      </div>

      <span slot="footer">
        <button
          @click="showBatchImport = false"
          class="px-4 py-2 border border-slate-200 text-slate-600 text-sm rounded-xl hover:bg-slate-50 transition-colors"
        >
          关闭
        </button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { logAction } from '../../services/audit'
import { EXPERTS_MASTER } from '../../data/experts'
import { PERMISSIONS } from '../../data/adminConfig'
import * as XLSX from 'xlsx'

export default {
  name: 'ExpertManager',

  data() {
    return {
      searchQuery: '',
      showBatchImport: false,
      importResult: null,
      PERMISSIONS,
    }
  },

  computed: {
    ...mapState(['cases']),
    ...mapGetters(['expertsWithDerived', 'hasPermission']),

    filteredExperts() {
      let list = [...(this.expertsWithDerived || [])]

      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase()
        list = list.filter(e =>
          e.name?.toLowerCase().includes(q) ||
          e.title?.toLowerCase().includes(q) ||
          e.department?.toLowerCase().includes(q) ||
          e.topics?.some(t => t.toLowerCase().includes(q))
        )
      }

      return list
    },
  },

  methods: {
    hasPerm(permission) {
      return this.hasPermission(permission)
    },

    statusLabel(status) {
      const labels = {
        active: '正常',
        suspended: '停用',
        banned: '禁用',
      }
      return labels[status] || status
    },

    handleAdd() {
      this.$router.push('/admin/experts/edit')
    },

    handleEdit(row) {
      this.$router.push(`/admin/experts/edit?id=${row.id}`)
    },

    handleDelete(row) {
      this.$confirm(`确定删除专家「${row.name}」？此操作不可恢复。`, '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        const list = (this.expertsWithDerived || []).filter(e => e.id !== row.id)
        this.$store.commit('SET_EXPERTS', list)

        logAction({
          action: 'DELETE',
          targetType: 'expert',
          targetId: row.id,
          reason: `删除专家: ${row.name}`,
          source: 'admin-ui',
          actor: { role: this.$store.state.userRole },
        })

        this.$message.success('删除成功')
      }).catch(() => {})
    },

    downloadTemplate() {
      const headers = ['姓名', '头衔', '所属公司', '职位', '头像URL', '个人简介', '名言', '擅长领域（逗号分隔）', '标签（逗号分隔）', '履历（逗号分隔）']
      const example = ['张瑞敏', '前海尔集团CEO', '海尔', '首席执行官', 'https://example.com/avatar.jpg', '管理创新领域的资深专家...', '没有成功的企业，只有时代的企业', '执行力,绩效管理,组织变革', '执行力,HR管理', '海尔集团CEO,海尔创始人']

      const ws = XLSX.utils.aoa_to_sheet([headers, example])
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, '专家导入模板')
      XLSX.writeFile(wb, '专家导入模板.xlsx')
    },

    handleExport() {
      const list = this.filteredExperts || []
      const rows = list.map(e => ({
        ID: e.id,
        姓名: e.name,
        头衔: e.title,
        所属公司: e.department,
        职位: e.position,
        所在城市: e.location || '',
        头像URL: e.avatar,
        个人简介: e.bio,
        名言: e.quote,
        擅长领域: e.topics?.join(', '),
        标签: e.tags?.join(', '),
        履历: e.resume?.join(', '),
        案例文档: e.caseDocument,
        音频标题: e.audioTitle,
        音频URL: e.audioUrl,
        视频标题: e.videoTitle,
        视频URL: e.videoUrl,
        首页推荐: e.isFeatured ? '是' : '否',
        推荐权重: e.recommendWeight,
        状态: this.statusLabel(e.status),
        积分: e.points,
        内容数量: e.contentCount,
        点击数: e.stats?.clicks,
        播放数: e.stats?.plays,
        收藏数: e.stats?.bookmarks,
        点赞数: e.stats?.likes,
        评论数: e.stats?.comments,
      }))

      const ws = XLSX.utils.json_to_sheet(rows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, '专家列表')
      const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '')
      XLSX.writeFile(wb, `专家列表_${dateStr}.xlsx`)
    },

    handleFileSelect(event) {
      const file = event.target.files[0]
      if (!file) return

      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result)
          const workbook = XLSX.read(data, { type: 'array' })
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]]
          const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 })

          if (rows.length < 2) {
            this.$message.error('文件为空或格式不正确')
            return
          }

          const result = { success: 0, failed: 0, errors: [] }
          const newExperts = []
          const existingList = [...(this.expertsWithDerived || [])]

          for (let i = 1; i < rows.length; i++) {
            const row = rows[i]
            const name = row[0]?.toString().trim()

            if (!name) {
              result.failed++
              result.errors.push(`第 ${i + 1} 行：姓名不能为空`)
              continue
            }

            const expertId = `e${Date.now()}-${i}`
            newExperts.push({
              id: expertId,
              name,
              title: row[1]?.toString().trim() || '',
              department: row[2]?.toString().trim() || '',
              position: row[3]?.toString().trim() || '',
              avatar: row[4]?.toString().trim() || '',
              bio: row[5]?.toString().trim() || '',
              quote: row[6]?.toString().trim() || '',
              topics: row[7] ? row[7].toString().split(',').map(t => t.trim()).filter(Boolean) : [],
              tags: row[8] ? row[8].toString().split(',').map(t => t.trim()).filter(Boolean) : [],
              resume: row[9] ? row[9].toString().split(',').map(t => t.trim()).filter(Boolean) : [],
              caseDocument: '',
              audioTitle: '',
              videoTitle: '',
              recommendWeight: 0,
              isFeatured: false,
              status: 'active',
              points: 0,
              contentCount: 0,
              stats: { clicks: 0, plays: 0, bookmarks: 0, likes: 0, comments: 0 },
              createdAt: Date.now(),
            })
            result.success++
          }

          if (result.success > 0) {
            const combined = [...existingList, ...newExperts]
            this.$store.commit('SET_EXPERTS', combined)

            logAction({
              action: 'CREATE',
              targetType: 'expert',
              targetId: 'batch',
              reason: `批量导入专家: ${result.success} 条`,
              source: 'admin-ui',
              actor: { role: this.$store.state.userRole },
            })
          }

          this.importResult = result
          if (result.success > 0) {
            this.$message.success(`成功导入 ${result.success} 条专家`)
          }
          if (result.failed > 0) {
            this.$message.warning(`${result.failed} 条导入失败`)
          }
        } catch (err) {
          this.$message.error('文件解析失败：' + err.message)
        }
      }
      reader.readAsArrayBuffer(file)
      event.target.value = ''
    },
  },
}
</script>
